import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Icon } from '@ds/core';
import { explicarErroDoBrain } from '@/data/brain';
import type { AcaoDeTexto, IdiomaDoBrain } from '@/data/brain';
import { dados } from '@/data/porta';
import { estiloAncorado, useFecharPorFora, useRetanguloDaAncora, useSetasNoMenu, Dica } from './ui/camadas';

/**
 * IA Prompts — porte do `ComposerAiAssist` do Brain original: melhorar
 * gramática, melhorar texto, pedir um texto e traduzir o que está no campo, pela
 * função `brain/texto` (modelo rápido, conta no teto, não grava conversa). O
 * original traduzia para 38 idiomas; o servidor da VecchIA aceita os três do
 * Brain.
 */

const IDIOMAS: { valor: IdiomaDoBrain; rotulo: string }[] = [
    { valor: 'pt-BR', rotulo: 'Português (Brasil)' },
    { valor: 'en', rotulo: 'Inglês' },
    { valor: 'es', rotulo: 'Espanhol' },
];

export interface AssistenteDeTextoProps {
    texto: string;
    aoMudarTexto: (texto: string) => void;
    desabilitado: boolean;
    /** Onde os balões de prompt e tradução se ancoram (a caixa do campo). */
    ancoraDoBalao: HTMLElement | null;
    aoFocarCampo: () => void;
    idioma: IdiomaDoBrain;
    escuro: boolean;
    aoErro: (titulo: string, detalhe: string) => void;
}

export function AssistenteDeTexto({ texto, aoMudarTexto, desabilitado, ancoraDoBalao, aoFocarCampo, idioma, escuro, aoErro }: AssistenteDeTextoProps) {
    const [menuAberto, setMenuAberto] = useState(false);
    const [balao, setBalao] = useState<'prompt' | 'traduzir' | null>(null);
    const [pedido, setPedido] = useState('');
    const [destino, setDestino] = useState<IdiomaDoBrain>(idioma === 'pt-BR' ? 'en' : 'pt-BR');
    const [trabalhando, setTrabalhando] = useState(false);
    const gatilho = useRef<HTMLButtonElement>(null);
    const menu = useRef<HTMLDivElement>(null);
    const caixa = useRef<HTMLDivElement>(null);
    const campoDoPedido = useRef<HTMLTextAreaElement>(null);
    const setas = useSetasNoMenu();

    const rectDoMenu = useRetanguloDaAncora(gatilho.current, menuAberto);
    const rectDoBalao = useRetanguloDaAncora(ancoraDoBalao ?? gatilho.current, balao !== null);

    useFecharPorFora(menuAberto, (alvo) => Boolean(menu.current?.contains(alvo) || gatilho.current?.contains(alvo)), () => setMenuAberto(false), gatilho.current);
    useFecharPorFora(balao !== null, (alvo) => Boolean(caixa.current?.contains(alvo)), () => setBalao(null), gatilho.current);

    useEffect(() => {
        if (menuAberto) setTimeout(() => menu.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus(), 0);
    }, [menuAberto]);
    useEffect(() => {
        if (balao === 'prompt') setTimeout(() => campoDoPedido.current?.focus(), 0);
    }, [balao]);

    const transformar = async (acao: AcaoDeTexto, entrada: string, lingua?: IdiomaDoBrain) => {
        setMenuAberto(false);
        if (!entrada.trim()) {
            aoErro('Nada para trabalhar', 'Digite um texto no campo antes de usar a IA.');
            return;
        }
        setTrabalhando(true);
        try {
            const saida = await dados().brainTexto(acao, entrada, lingua);
            aoMudarTexto(saida);
            aoFocarCampo();
        } catch (e) {
            const explicado = explicarErroDoBrain(e);
            aoErro(explicado.titulo, explicado.detalhe);
        } finally {
            setTrabalhando(false);
        }
    };

    const aplicarPedido = () => {
        const instrucao = pedido.trim();
        setBalao(null);
        if (!instrucao) return;
        const entrada = texto.trim() ? `${instrucao}\n\nTexto atual:\n${texto}` : instrucao;
        void transformar('escrever', entrada);
        setPedido('');
    };

    const estiloDoBalao = (largura: number) => {
        if (!rectDoBalao) return { visibility: 'hidden' as const };
        const left = Math.max(12, Math.min(rectDoBalao.left + rectDoBalao.width / 2 - largura / 2, window.innerWidth - largura - 12));
        return { position: 'fixed' as const, left, bottom: window.innerHeight - rectDoBalao.top + 8, width: largura, maxWidth: 'calc(100vw - 24px)', zIndex: 2100 };
    };

    return (
        <>
            <Dica titulo="IA Prompts">
                <button
                    ref={gatilho} type="button" className="brain-shell__icon-btn" aria-label="IA Prompts" aria-haspopup="menu" aria-expanded={menuAberto}
                    onClick={(e) => { e.stopPropagation(); setMenuAberto((v) => !v); }}
                    disabled={desabilitado || trabalhando}
                >
                    <Icon name={trabalhando ? 'loader-circle' : 'sparkles'} size={13} strokeColor={escuro ? '#ffffff' : '#2563eb'} />
                </button>
            </Dica>

            {menuAberto && rectDoMenu && createPortal(
                // eslint-disable-next-line jsx-a11y/interactive-supports-focus -- o foco vai para os itens, não para o contêiner
                <div ref={menu} role="menu" aria-label="IA Prompts" className="brain-assistente__menu" onKeyDown={setas} style={{ ...estiloAncorado(rectDoMenu, { minWidth: 200, placement: 'above', offset: 6 }), zIndex: 2100 }}>
                    <button type="button" role="menuitem" className="brain-assistente__item" onClick={() => void transformar('gramatica', texto)}>Melhorar gramática</button>
                    <button type="button" role="menuitem" className="brain-assistente__item" onClick={() => void transformar('melhorar', texto)}>Melhorar texto</button>
                    <button type="button" role="menuitem" className="brain-assistente__item" onClick={() => { setMenuAberto(false); setPedido(''); setBalao('prompt'); }}>Prompt (pedir um texto)</button>
                    <button type="button" role="menuitem" className="brain-assistente__item" onClick={() => { setMenuAberto(false); setBalao('traduzir'); }}>Traduzir</button>
                </div>,
                document.body,
            )}

            {balao === 'prompt' && createPortal(
                <div ref={caixa} className="brain-assistente__balao" role="dialog" aria-label="Prompt do Agente" style={estiloDoBalao(328)}>
                    <div className="brain-assistente__cabecalho">
                        <span>Prompt do Agente</span>
                        <p>Descreva o que a IA deve gerar ou alterar no texto</p>
                    </div>
                    <div className="brain-assistente__corpo">
                        <textarea ref={campoDoPedido} className="brain-assistente__campo" placeholder="Descreva o que a IA deve fazer…" aria-label="O que a IA deve fazer" rows={4} maxLength={2000} value={pedido} onChange={(e) => setPedido(e.target.value)} />
                        <div className="brain-assistente__acoes">
                            <button type="button" className="brain-assistente__cancelar" onClick={() => setBalao(null)}>Cancelar</button>
                            <button type="button" className="brain-assistente__aplicar" onClick={aplicarPedido} disabled={trabalhando}>{trabalhando ? 'Processando…' : 'Aplicar'}</button>
                        </div>
                    </div>
                </div>,
                document.body,
            )}

            {balao === 'traduzir' && createPortal(
                <div ref={caixa} className="brain-assistente__balao brain-assistente__balao--traduzir" role="dialog" aria-label="Traduzir" style={estiloDoBalao(272)}>
                    <div className="brain-assistente__cabecalho brain-assistente__cabecalho--compacto">
                        <span>Traduzir</span>
                        <p>Escolha o idioma de destino</p>
                    </div>
                    <div className="brain-assistente__corpo">
                        <label className="brain-assistente__rotulo" htmlFor="brain-traduzir-idioma">Idioma</label>
                        <select id="brain-traduzir-idioma" className="brain-assistente__select" value={destino} onChange={(e) => setDestino(e.target.value as IdiomaDoBrain)}>
                            {IDIOMAS.map((i) => <option key={i.valor} value={i.valor}>{i.rotulo}</option>)}
                        </select>
                        <div className="brain-assistente__acoes">
                            <button type="button" className="brain-assistente__cancelar" onClick={() => setBalao(null)}>Cancelar</button>
                            <button type="button" className="brain-assistente__aplicar" onClick={() => { setBalao(null); void transformar('traduzir', texto, destino); }} disabled={trabalhando}>
                                {trabalhando ? 'Processando…' : 'Traduzir'}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body,
            )}
        </>
    );
}
